import type { StateCreator } from "zustand";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { showToast } from "../../lib/toast";
import { setStaleBadge } from "../../lib/badge";
import { selectStaleTasks } from "../../lib/stale";
import type { ITask, ITaskInsert, ITaskUpdate } from "../../types";
import { ZTask } from "../../types/zod-schemas";
import type { IStoreState } from "../useStore";

/** Track in-flight enrichment calls to prevent duplicate re-enrichment. */
const enrichingUuids = new Set<string>();

export interface TaskSlice {
  tasks: ITask[];
  sharedTasks: ITask[];
  channels: RealtimeChannel[];

  fetchTasks: () => Promise<void>;
  createTask: (rawInput: string, url?: string | null, tagId?: string | null) => Promise<ITask | null>;
  createPrerequisiteTask: (
    rawInput: string,
    dependentTaskUuid: string,
    opts?: { tagId?: string | null; status?: "backlog" | "today" }
  ) => Promise<ITask | null>;
  updateTask: (taskUuid: string, updates: ITaskUpdate) => Promise<void>;
  deleteTask: (taskUuid: string) => Promise<void>;
  moveToToday: (taskUuid: string) => Promise<void>;
  moveToBacklog: (taskUuid: string) => Promise<void>;
  completeTask: (taskUuid: string, cascadePrereqUuids?: string[]) => Promise<void>;
  uncompleteTask: (taskUuid: string) => Promise<void>;
  archiveTask: (taskUuid: string) => Promise<void>;
  snoozeTask: (taskUuid: string, days: number) => Promise<void>;
  markNudged: (taskUuid: string) => Promise<void>;
  enrichTask: (taskUuid: string) => Promise<void>;
  subscribeToRealtime: () => void;
  subscribeToSharedRealtime: () => void;
  unsubscribeFromRealtime: () => void;
  fetchSharedTasks: () => Promise<void>;
  createSharedTask: (rawInput: string, tagId: string, url?: string | null) => Promise<ITask | null>;
}

export const createTaskSlice: StateCreator<
  IStoreState,
  [["zustand/devtools", never]],
  [],
  TaskSlice
> = (set, get) => ({
  tasks: [],
  sharedTasks: [],
  channels: [],

  fetchTasks: async () => {
    const { user } = get();
    if (!user) return;

    set({ loading: { ...get().loading, tasks: true } });

    const { data, error } = await supabase
      .from("tasks")
      .select("*")
      .eq("user_uuid", user.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (!error && data) {
      set({ tasks: data });
      setStaleBadge(selectStaleTasks(data).length);

      // Recover tasks stuck with ai_enriched=false for over 2 minutes
      const STALE_MS = 2 * 60 * 1000;
      const now = Date.now();
      const stale = data.filter(
        (t) =>
          !t.ai_enriched &&
          !enrichingUuids.has(t.uuid) &&
          now - new Date(t.created_at).getTime() > STALE_MS
      );
      for (const t of stale) {
        get().enrichTask(t.uuid);
      }
    }

    set({ loading: { ...get().loading, tasks: false } });
  },

  createTask: async (rawInput: string, url?: string | null, tagId?: string | null) => {
    const { user } = get();
    if (!user) return null;

    const insert: ITaskInsert = {
      raw_input: rawInput,
      user_uuid: user.id,
      status: "backlog",
      source: "manual",
      ...(url ? { url } : {}),
      ...(tagId ? { tag_id: tagId } : {}),
    };

    const { data, error } = await supabase
      .from("tasks")
      .insert(insert)
      .select()
      .single();

    if (error) {
      console.error("Failed to create task:", error);
      showToast("Failed to create task");
      return null;
    }

    set({ tasks: [data, ...get().tasks] });
    get().enrichTask(data.uuid);
    return data;
  },

  createPrerequisiteTask: async (
    rawInput: string,
    dependentTaskUuid: string,
    opts?: { tagId?: string | null; status?: "backlog" | "today" }
  ) => {
    const { user, tasks } = get();
    if (!user) return null;

    const parent = tasks.find((t) => t.uuid === dependentTaskUuid);
    const status: "backlog" | "today" =
      opts?.status ??
      (parent?.status === "today" ? "today" : "backlog");

    const insert: ITaskInsert = {
      raw_input: rawInput,
      user_uuid: user.id,
      status,
      source: "manual",
      ...(opts?.tagId ? { tag_id: opts.tagId } : parent?.tag_id ? { tag_id: parent.tag_id } : {}),
    };

    const { data, error } = await supabase
      .from("tasks")
      .insert(insert)
      .select()
      .single();

    if (error || !data) {
      console.error("Failed to create prerequisite task:", error);
      showToast("Failed to create prerequisite");
      return null;
    }

    // If created in "today" status, set moved_to_today_at for consistency
    if (status === "today") {
      const nowIso = new Date().toISOString();
      await supabase
        .from("tasks")
        .update({ moved_to_today_at: nowIso })
        .eq("uuid", data.uuid);
      data.moved_to_today_at = nowIso;
    }

    set({ tasks: [data, ...get().tasks] });

    // Link as prerequisite
    const ok = await get().addPrerequisite(data.uuid, dependentTaskUuid);
    if (!ok) {
      // If linking fails (e.g. cycle), roll back the task
      await supabase
        .from("tasks")
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: user.id,
        })
        .eq("uuid", data.uuid);
      set({ tasks: get().tasks.filter((t) => t.uuid !== data.uuid) });
      return null;
    }

    get().enrichTask(data.uuid);
    return data;
  },

  updateTask: async (taskUuid: string, updates: ITaskUpdate) => {
    const { error } = await supabase
      .from("tasks")
      .update(updates)
      .eq("uuid", taskUuid);

    if (error) {
      console.error("Failed to update task:", error);
      showToast("Failed to update task");
      return;
    }

    set({
      tasks: get().tasks.map((t) =>
        t.uuid === taskUuid ? { ...t, ...updates } : t
      ),
    });
  },

  deleteTask: async (taskUuid: string) => {
    const { user } = get();
    if (!user) return;

    const { error } = await supabase
      .from("tasks")
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user.id,
      })
      .eq("uuid", taskUuid);

    if (error) {
      console.error("Failed to delete task:", error);
      showToast("Failed to delete task");
      return;
    }

    set({ tasks: get().tasks.filter((t) => t.uuid !== taskUuid) });

    // Soft-delete dependencies involving this task so chains skip over it
    await get().breakDependenciesForTasks([taskUuid]);
  },

  moveToToday: async (taskUuid: string) => {
    // Guard: only the "top" of a dependency stack (no incomplete prereqs) can move to Today.
    const { dependencies, tasks } = get();
    const incomplete = dependencies.filter((d) => {
      if (d.dependent_uuid !== taskUuid) return false;
      const prereq = tasks.find((t) => t.uuid === d.prerequisite_uuid);
      if (!prereq) return false;
      return prereq.status !== "done" && prereq.status !== "archived";
    });
    if (incomplete.length > 0) {
      showToast("Complete prerequisites first");
      return;
    }

    await get().updateTask(taskUuid, {
      status: "today",
      moved_to_today_at: new Date().toISOString(),
    });
  },

  moveToBacklog: async (taskUuid: string) => {
    await get().updateTask(taskUuid, {
      status: "backlog",
      moved_to_today_at: null,
    });
  },

  completeTask: async (taskUuid: string, cascadePrereqUuids?: string[]) => {
    const task = get().tasks.find((t) => t.uuid === taskUuid);
    const nowIso = new Date().toISOString();

    // Batch-complete the main task + any cascading prereqs
    const toComplete = [taskUuid, ...(cascadePrereqUuids ?? [])];
    const { error } = await supabase
      .from("tasks")
      .update({ status: "done", completed_at: nowIso })
      .in("uuid", toComplete);

    if (error) {
      console.error("Failed to complete task:", error);
      showToast("Failed to complete task");
      return;
    }

    set({
      tasks: get().tasks.map((t) =>
        toComplete.includes(t.uuid)
          ? { ...t, status: "done" as const, completed_at: nowIso }
          : t
      ),
    });
    setStaleBadge(selectStaleTasks(get().tasks).length);

    // Break the dependency chain (soft-delete all active deps involving any completed task)
    await get().breakDependenciesForTasks(toComplete);

    // Fire-and-forget: notify Slack thread if task came from Slack
    if (task?.source === "slack") {
      supabase.functions
        .invoke("slack-notify", { body: { task_uuid: taskUuid } })
        .then((res) => {
          if (res.error) console.error("Slack notify error:", res.error);
        })
        .catch((err) => console.error("Slack notify failed:", err));
    }
  },

  uncompleteTask: async (taskUuid: string) => {
    await get().updateTask(taskUuid, {
      status: "today",
      completed_at: null,
    });
  },

  archiveTask: async (taskUuid: string) => {
    await get().updateTask(taskUuid, {
      status: "archived",
      archived_at: new Date().toISOString(),
    });
  },

  snoozeTask: async (taskUuid: string, days: number) => {
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    const nowIso = new Date().toISOString();
    await get().updateTask(taskUuid, {
      snooze_until: until,
      last_nudged_at: nowIso,
    });
    setStaleBadge(selectStaleTasks(get().tasks).length);
  },

  markNudged: async (taskUuid: string) => {
    await get().updateTask(taskUuid, {
      last_nudged_at: new Date().toISOString(),
    });
    setStaleBadge(selectStaleTasks(get().tasks).length);
  },

  enrichTask: async (taskUuid: string) => {
    if (enrichingUuids.has(taskUuid)) return;
    enrichingUuids.add(taskUuid);

    const { session } = get();

    const fallback = async () => {
      const task = get().tasks.find((t) => t.uuid === taskUuid);
      const fallbackTitle = task?.raw_input ?? "Untitled";

      await supabase
        .from("tasks")
        .update({ ai_enriched: true, title: fallbackTitle })
        .eq("uuid", taskUuid);

      set({
        tasks: get().tasks.map((t) =>
          t.uuid === taskUuid
            ? { ...t, ai_enriched: true, title: t.raw_input }
            : t
        ),
      });
    };

    if (!session) {
      await fallback();
      return;
    }

    try {
      const res = await supabase.functions.invoke("enrich-task", {
        body: { task_uuid: taskUuid },
        ...(typeof AbortSignal.timeout === "function"
          ? { signal: AbortSignal.timeout(30_000) }
          : {}),
      });

      if (res.error) {
        console.error("Enrichment failed:", res.error);
        await fallback();
        return;
      }

      const data = res.data;
      const first = data.tasks?.[0];
      if (!first) {
        await fallback();
        return;
      }

      if (data.reassigned) {
        set({ tasks: get().tasks.filter((t) => t.uuid !== taskUuid) });
        return;
      }

      if (data.recurring_definition_created) {
        get().fetchRecurringDefinitions();
      }

      const updatedTasks = get().tasks.map((t) =>
        t.uuid === taskUuid
          ? {
              ...t,
              title: first.title,
              description: first.description,
              urgency: first.urgency,
              importance: first.importance,
              ai_enriched: true,
              ai_enriched_at: new Date().toISOString(),
              ...(first.due_date ? { due_date: first.due_date } : {}),
              ...(first.lead_time ? { lead_time: first.lead_time } : {}),
              ...(data.tag_id ? { tag_id: data.tag_id } : {}),
              ...(data.recurring_def_id ? { recurring_def_id: data.recurring_def_id, source: "recurring" as const } : {}),
            }
          : t
      );

      const additional = data.additionalTasks ?? [];
      const existingUuids = new Set(updatedTasks.map((t) => t.uuid));
      const newTasks = additional.filter(
        (t: ITask) => !existingUuids.has(t.uuid)
      );

      set({ tasks: [...newTasks, ...updatedTasks] });
    } catch (err) {
      console.error("Enrichment error:", err);
      await fallback();
    } finally {
      enrichingUuids.delete(taskUuid);
    }
  },

  subscribeToRealtime: () => {
    const { user } = get();
    if (!user) return;

    const channel = supabase
      .channel("tasks-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          filter: `user_uuid=eq.${user.id}`,
        },
        (payload) => {
          const { eventType } = payload;

          if (eventType === "INSERT") {
            try {
              const task = ZTask.parse(payload.new);
              const exists = get().tasks.some((t) => t.uuid === task.uuid);
              if (!exists && !task.deleted_at) {
                set({ tasks: [task, ...get().tasks] });
              }
            } catch (e) {
              console.error("Invalid realtime task payload:", e);
            }
          }

          if (eventType === "UPDATE") {
            try {
              const task = ZTask.parse(payload.new);
              if (task.deleted_at) {
                set({
                  tasks: get().tasks.filter((t) => t.uuid !== task.uuid),
                  sharedTasks: get().sharedTasks.filter((t) => t.uuid !== task.uuid),
                });
              } else {
                const mergeTask = (existing: ITask) => {
                  if (existing.uuid !== task.uuid) return existing;
                  // Don't overwrite enriched data with un-enriched
                  if (existing.ai_enriched && !task.ai_enriched) return existing;
                  return task;
                };
                set({
                  tasks: get().tasks.map(mergeTask),
                  sharedTasks: get().sharedTasks.map(mergeTask),
                });
              }
            } catch (e) {
              console.error("Invalid realtime task payload:", e);
            }
          }

          if (eventType === "DELETE") {
            const old = payload.old as { uuid?: string };
            if (old.uuid) {
              set({
                tasks: get().tasks.filter((t) => t.uuid !== old.uuid),
              });
            }
          }
        }
      )
      .subscribe();

    set({ channels: [channel] });
  },

  subscribeToSharedRealtime: () => {
    const { sharedTags, channels } = get();
    if (sharedTags.length === 0) return;

    const newChannels: RealtimeChannel[] = [];

    for (const st of sharedTags) {
      const channel = supabase
        .channel(`shared-tasks-${st.tag_id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "tasks",
            filter: `tag_id=eq.${st.tag_id}`,
          },
          (payload) => {
            const { eventType } = payload;

            if (eventType === "INSERT") {
              try {
                const task = ZTask.parse(payload.new);
                const exists = get().sharedTasks.some((t) => t.uuid === task.uuid);
                if (!exists && !task.deleted_at) {
                  set({ sharedTasks: [task, ...get().sharedTasks] });
                }
              } catch (e) {
                console.error("Invalid shared task payload:", e);
              }
            }

            if (eventType === "UPDATE") {
              try {
                const task = ZTask.parse(payload.new);
                if (task.deleted_at) {
                  set({ sharedTasks: get().sharedTasks.filter((t) => t.uuid !== task.uuid) });
                } else {
                  set({
                    sharedTasks: get().sharedTasks.map((t) => {
                      if (t.uuid !== task.uuid) return t;
                      if (t.ai_enriched && !task.ai_enriched) return t;
                      return task;
                    }),
                  });
                }
              } catch (e) {
                console.error("Invalid shared task payload:", e);
              }
            }

            if (eventType === "DELETE") {
              const old = payload.old as { uuid?: string };
              if (old.uuid) {
                set({ sharedTasks: get().sharedTasks.filter((t) => t.uuid !== old.uuid) });
              }
            }
          }
        )
        .subscribe();

      newChannels.push(channel);
    }

    set({ channels: [...channels, ...newChannels] });
  },

  unsubscribeFromRealtime: () => {
    const { channels } = get();
    channels.forEach((ch) => supabase.removeChannel(ch));
    set({ channels: [] });
  },

  fetchSharedTasks: async () => {
    const { sharedTags } = get();
    if (sharedTags.length === 0) {
      set({ sharedTasks: [], loading: { ...get().loading, sharedTasks: false } });
      return;
    }

    set({ loading: { ...get().loading, sharedTasks: true } });

    const sharedTagIds = sharedTags.map((st) => st.tag_id);

    const { data, error } = await supabase
      .from("tasks")
      .select("*")
      .in("tag_id", sharedTagIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (!error && data) {
      set({ sharedTasks: data });
    }

    set({ loading: { ...get().loading, sharedTasks: false } });
  },

  createSharedTask: async (rawInput: string, tagId: string, url?: string | null) => {
    const res = await supabase.functions.invoke("create-shared-task", {
      body: { raw_input: rawInput, tag_id: tagId, ...(url ? { url } : {}) },
    });

    if (res.error || !res.data?.task) {
      console.error("Failed to create shared task:", res.error);
      showToast("Failed to create shared task");
      return null;
    }

    const task = res.data.task;
    set({ sharedTasks: [task, ...get().sharedTasks] });

    try {
      await supabase.functions.invoke("enrich-task", {
        body: { task_uuid: task.uuid },
      });
    } catch {
      // Enrichment may fail if caller doesn't own the task — that's expected
    }

    return task;
  },
});
